import * as Sentry from "@sentry/vue";
import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
} from "vue-router";
import { routes } from "vue-router/auto-routes";

import DefaultLayout from "@/layouts/DefaultLayout.vue";
import type { AdminApp } from "@/misc/types";
import { tryGetUserData } from "@/plugins/auth";
import type { Permission } from "@/roles/types";
import { errorMessagesIndicatingOldAppVersion } from "@/sentry/errorMessages";

type PermissionProtectedRoute = RouteRecordRaw & {
  meta: RouteRecordRaw["meta"] & {
    auth: Permission[] | { roles: Permission[] };
  };
};

export const routerPlugin = (app: AdminApp) => {
  const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
      if (savedPosition) {
        return savedPosition;
      } else {
        if (to.path === from.path) {
          return false;
        }

        return { top: 0 };
      }
    },
    routes: routes as PermissionProtectedRoute[],
  });

  router.beforeEach(async (to) => {
    to.meta.layout = to.meta.layout || DefaultLayout;

    const authRules = to.meta.auth;
    if (!authRules || !Array.isArray(authRules)) {
      return;
    }

    if (authRules.length === 0) return;

    const user = await tryGetUserData(app);

    if (!user) return "/login";

    const hasAccess = authRules.some((rule) => user.permissions.includes(rule));
    if (!hasAccess) {
      return {
        name: "/403",
        query: {
          requiredPermissions: authRules,
        },
      };
    }
  });

  router.onError((error, to) => {
    if (isChunkLoadError(error)) {
      reloadOnceThenGoTo(to.fullPath);

      return;
    }

    // eslint-disable-next-line no-console
    console.error(error);
    Sentry.captureException(error);
  });

  router.afterEach(() => {
    sessionStorage.removeItem(RELOAD_GUARD_KEY);
  });

  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
    reloadOnceThenGoTo(window.location.pathname + window.location.search);
  });

  app.router = router;
  app.use(router);
};

// Reload once on a stale-chunk 404 (old build's hash gone after a new deploy) instead of a dead navigation.
const RELOAD_GUARD_KEY = "admin-portal:chunk-reload";
const SAFE_PATH = /^\/(?!\/)/;

function isChunkLoadError(error: unknown) {
  const message = error instanceof Error ? error.message : String(error);

  return errorMessagesIndicatingOldAppVersion.some((m) => message.includes(m));
}

function reloadOnceThenGoTo(fullPath: string) {
  if (sessionStorage.getItem(RELOAD_GUARD_KEY) === fullPath) {
    return;
  }

  sessionStorage.setItem(RELOAD_GUARD_KEY, fullPath);

  if (SAFE_PATH.test(fullPath)) {
    window.location.assign(fullPath);
  } else {
    window.location.reload();
  }
}
